import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Card, CardBody, Input, Button, Divider } from "@heroui/react";
import { useAuthStore } from "../../store/authStore";
import authMethods from "../../api/methods/auth";
import { Key, Mail } from "lucide-react";

const LoginPage: React.FC = () => {
  const navigate = useNavigate();
  const login = useAuthStore((state) => state.login);

  const [formData, setFormData] = useState({
    email: "",
    otp: "",
  });
  const [step, setStep] = useState<"email" | "otp">("email");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");

  const handleEmailSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError("");

    try {
      await authMethods.login(formData.email);
      setStep("otp");
    } catch (err: any) {
      setError(err.response?.data?.message || "Failed to send OTP");
    } finally {
      setIsLoading(false);
    }
  };

  const handleOTPSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError("");

    try {
      const response = await authMethods.verifyOtp({
        email: formData.email,
        otp: formData.otp,
      });

      // Parse the response structure based on your API
      const { token, user_info } = response.data;

      login(token.access, token.refresh, user_info);
      navigate("/dashboard");
    } catch (err: any) {
      setError(err.response?.data?.message || "Invalid OTP");
    } finally {
      setIsLoading(false);
    }
  };

  const handleInputChange = (field: string, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  return (
    <div
      className="min-h-screen flex items-center justify-center px-4 bg-cover bg-center bg-no-repeat"
      style={{ backgroundImage: "url(/Login-bg.jpg)" }}
    >
      <Card className="w-full max-w-md relative z-10 shadow-2xl">
        <CardBody className="p-8">
          {/* Logo */}
          <div className="flex justify-center mb-6">
            <img
              src="/connectifyz.png"
              alt="Connectifyz Logo"
              className="h-16 w-auto"
            />
          </div>

          <div className="text-center mb-8">
            <h1 className="text-2xl font-bold text-gray-900">Welcome Back</h1>
            <p className="text-gray-600 mt-2">
              {step === "email"
                ? "Enter your email address to continue"
                : "Enter the OTP sent to your email"}
            </p>
          </div>

          {error && (
            <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-lg mb-6">
              {error}
            </div>
          )}

          {step === "email" ? (
            <form onSubmit={handleEmailSubmit} className="space-y-6">
              <Input
                label="Email Address"
                placeholder="your@email.com"
                value={formData.email}
                onChange={(e) => handleInputChange("email", e.target.value)}
                isRequired
                type="email"
                startContent={
                  <div className="pointer-events-none flex items-center">
                    <Mail size={18} className="text-default-400" />
                  </div>
                }
              />

              <Button
                type="submit"
                color="primary"
                className="w-full"
                isLoading={isLoading}
                isDisabled={!formData.email.trim()}
              >
                Send OTP
              </Button>
            </form>
          ) : (
            <form onSubmit={handleOTPSubmit} className="space-y-6">
              <Input
                label="Verification Code"
                placeholder="Enter 4-digit code"
                value={formData.otp}
                onChange={(e) => handleInputChange("otp", e.target.value)}
                isRequired
                className="tracking-widest"
                maxLength={4}
                startContent={
                  <div className="pointer-events-none flex items-center">
                    <Key size={18} className="text-default-400" />
                  </div>
                }
              />

              <div className="flex gap-3">
                <Button
                  variant="bordered"
                  className="flex-1"
                  onPress={() => setStep("email")}
                >
                  Back
                </Button>
                <Button
                  type="submit"
                  color="primary"
                  className="flex-1"
                  isLoading={isLoading}
                  isDisabled={formData.otp.length !== 4}
                >
                  Verify
                </Button>
              </div>
            </form>
          )}

          <Divider className="my-6" />

          <div className="text-center">
            <p className="text-gray-600">
              Don't have an account?{" "}
              <Link
                to="/auth/register"
                className="text-blue-600 hover:text-blue-800 font-medium"
              >
                Sign up
              </Link>
            </p>
          </div>
        </CardBody>
      </Card>
    </div>
  );
};

export default LoginPage;
